import React, { useContext, useState, useRef, useEffect, useCallback, startTransition } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
  Checkbox,
  Chip,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { WasteChecksheetDetailContext, WasteChecksheetDetailValues } from '../contexts/WasteChecksheetDetailContext';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SEARCH_DEBOUNCE_MS = 300;

const WasteChecksheetSearchFilterBar = () => {
  const {
    searchText,
    setSearchText,
    selectedCategories,
    setSelectedCategories,
    allCategoryNames,
    isMobile,
    t,
  } = useContext(WasteChecksheetDetailContext) as WasteChecksheetDetailValues;

  const theme = useTheme();

  // Local state for immediate input responsiveness; debounce propagation to context
  const [localSearchText, setLocalSearchText] = useState(searchText);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setLocalSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      startTransition(() => { setSearchText(value); });
    }, SEARCH_DEBOUNCE_MS);
  }, [setSearchText]);

  // Sync local state when parent resets searchText externally
  useEffect(() => { setLocalSearchText(searchText); }, [searchText]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleCategoryChange = useCallback((_event: React.SyntheticEvent, newValue: string[]) => {
    startTransition(() => { setSelectedCategories(newValue); });
  }, [setSelectedCategories]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 4,
        px: 5,
        pb: 4,
      }}
    >
      <TextField
        size="small"
        placeholder={t('filterPanel.search')}
        value={localSearchText}
        onChange={handleSearchChange}
        sx={{ minWidth: isMobile ? '100%' : '280px', flex: isMobile ? undefined : '0 0 280px' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.palette.custom.gray[400] }} />
            </InputAdornment>
          ),
        }}
      />
      <Autocomplete
        multiple
        size="small"
        options={allCategoryNames}
        disableCloseOnSelect
        value={selectedCategories}
        onChange={handleCategoryChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              sx={{
                backgroundColor: theme.palette.custom.gray[100],
                fontSize: '0.75rem',
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedCategories.length === 0 ? t('filterPanel.title') : ''}
          />
        )}
        sx={{ minWidth: isMobile ? '100%' : '300px', flex: 1 }}
      />
    </Box>
  );
};

export default WasteChecksheetSearchFilterBar;
